import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useContext, useMemo, useEffect, useState } from "react";
import { useGetCalendarEvents, useGetCalendar } from "src/services";
import ical from "ical.js";
import { Box, Typography, Button, Link } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute, getApiRoute } from "src/services";
import { customEvent } from "src/utils/gtag.js";
import UAParser from "ua-parser-js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import {
  generateGCalReminder,
  getCalendarApiUrl,
  getWebcalFormat,
  useCheckSubscriptionStatus,
  copyCalendarLinkToClipboard,
  handleOpenWebcal,
  safeParse,
} from "src/utils/Utils.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import LoadingAnimation from "src/components/blocks/LoadingAnimation.js";
import SignInDialog from "src/components/blocks/dialogs/SignInDialog.js";
import ActionCard from "src/components/blocks/ActionCard.js";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import TroubleshootingSection from "../troubleshooting/TroubleshootingSection.js";

const SimpleVerifyCalendarSubscription = () => {
  const { user } = useContext(AuthContext);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const location = useLocation();
  const { handle, calendarId } = useParams();
  const [searchParams] = useSearchParams();
  const [calendar, setCalendar] = useState(location.state?.calendar ?? null);
  const email = decodeURIComponent(searchParams.get("email") || "");
  const isReminder =
    safeParse(searchParams.get("is_reminder")) ??
    location.state?.isReminder ??
    false;
  const navigate = useNavigate();
  const uaParser = new UAParser();
  const parserResults = uaParser.getResult();
  const theme = useTheme();
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  // Detect calendar provider
  const getCalendarProvider = () => {
    if (user?.provider === "email") {
      // For email users, detect based on device/browser
      const os = parserResults.os?.name?.toLowerCase();
      const browser = parserResults.browser?.name?.toLowerCase();

      if (os?.includes("android") || browser?.includes("chrome")) {
        return "google";
      }
      if (
        os?.includes("ios") ||
        os?.includes("mac") ||
        browser?.includes("safari")
      ) {
        return "apple";
      }
      return "google"; // default fallback
    }
    return user?.provider || "google"; // return the actual provider or default to google
  };

  const [selectedProvider, setSelectedProvider] = useState(
    getCalendarProvider()
  );

  const { isFetching: isLoadingCalendar, isError: isCalendarError } =
    useGetCalendar(
      { handle, calendarId },
      {
        enabled: !!user && !calendar && !!handle && !!calendarId,
        onSuccess: (data) => {
          if (!data) return;
          setCalendar(data);
        },
        onError: (error) => {
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            "An error occurred loading the calendar.",
            "error"
          );
          console.error("Error fetching calendar:", error);
        },
      }
    );

  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const { inGroup } = useCheckSubscriptionStatus({
    user,
    calendar,
  });

  const { mutateAsync: verifyCalendar, isLoading: isVerifying } = useMutation({
    mutationFn: async (calendarId) => {
      return await axios.post(
        getApiRoute("user", "VERIFY_CALENDAR"),
        { calendarId },
        { withCredentials: true }
      );
    },
  });

  const link = useMemo(
    () => calendar?.stanzaLink?.replace("webcal://", "http://") || "",
    [calendar?.stanzaLink]
  );

  const { data: calendarEvents, isLoading: isLoadingCalendarEvents } =
    useGetCalendarEvents(
      {
        link,
        updated: calendar?.updatedAt,
      },
      {
        enabled: !!user && !!calendar?.stanzaLink,
      }
    );

  // Show modal on mount if guest
  useEffect(() => {
    if (!user) setOpenSignInDialog(true);
  }, [user]);

  const mostRecentEventDate = useMemo(() => {
    if (!calendarEvents) return null;

    try {
      const jcalData = ical.parse(calendarEvents);
      const comp = new ical.Component(jcalData);
      const vevents = comp.getAllSubcomponents("vevent");

      if (vevents.length === 0) return null;

      const now = new Date();
      let closestEvent = null;
      let smallestDiff = Infinity;

      vevents.forEach((event) => {
        const end = event.getFirstPropertyValue("dtend").toJSDate();
        // Only consider events that have not yet ended
        if (end > now) {
          const diff = end - now;
          if (diff < smallestDiff) {
            smallestDiff = diff;
            closestEvent = event;
          }
        }
      });

      if (!closestEvent) return null;

      const eventDate = closestEvent
        .getFirstPropertyValue("dtstart")
        .toJSDate();
      return eventDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [calendarEvents]);

  const handleConfirm = async () => {
    try {
      // Track the "Yes, I see it" button click
      customEvent({
        name: "user_confirm_calendar_subscription",
        category: "calendar_subscription",
        label: isReminder ? "confirm_reminder" : "confirm_calendar",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          calendar_handle: calendar?.handle,
        },
      });

      if (user && calendar?._id) {
        await verifyCalendar(calendar._id);
        setConfirmed(true);
      }

      navigate(getPageRoute("help", "SUBSCRIBING_CALENDAR_SUCCESS"), {
        state: {
          calendar,
          isReminder,
        },
      });
    } catch (error) {
      console.error("Error verifying calendar:", error);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Failed to verify calendar. Please try again.",
        "error"
      );
    }
  };

  const handleResubscribe = () => {
    if (isReminder) {
      // Track the "Download the reminder again" click
      customEvent({
        name: "user_error_download_reminder_again",
        category: "CalendarSubscription",
        label: "Download Reminder Again",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendar?._id,
          calendar_handle: calendar?.handle,
        },
      });

      // Handle downloading reminder again
      const stanzaLink = `${calendar?.stanzaLink}/${user?._id}.ics`;
      const icsContent = generateGCalReminder(calendar?.name, stanzaLink);
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = `Sync_${calendar?.name}_GCal_Reminder.ics`;

      // Append to the document and trigger the click
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Track the "Resubscribe" click
      customEvent({
        name: "user_error_resubscribe_calendar",
        category: "CalendarSubscription",
        label: "Resubscribe to Calendar",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendar?._id,
          calendar_handle: calendar?.handle,
        },
      });

      // Handle resubscribing to calendar
      handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
    }
  };

  const handleCopyToClipboard = () => {
    copyCalendarLinkToClipboard({
      inGroup,
      user,
      calendar,
      showSnackBar,
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
    });
  };

  useEffect(() => {
    customEvent({
      name: "landed_on_subscription_verification",
      category: "CalendarSubscription",
      label: "Landed on Subscription Verification",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  const guestMainTitle = "Verify Your Subscription";
  const guestSubtitle = email ? (
    <>
      Please sign in using the email <b>{email}</b>
    </>
  ) : (
    "Please sign in to continue."
  );

  return (
    <>
      {isLoadingCalendar || isLoadingCalendarEvents ? (
        <LoadingAnimation />
      ) : isCalendarError ? (
        <ActionCard
          id="calendar-not-found"
          data-testid="calendar-not-found"
          title="Calendar not found"
          description="The calendar you are looking for does not exist."
          primaryButtonCTA="Go to Stanza"
          primaryAction={() => navigate(getPageRoute("home", "HOME"))}
          iconName="calendar_month"
        />
      ) : (
        <InstructionsLayout
          sx={{
            alignItems: "flex-start",
            textAlign: "left",
            width: "100%",
            boxSizing: "border-box",
            "&>div": {
              maxWidth: "100%",
            },
          }}
        >
          <Box
            sx={{
              mt: "20%",
            }}
          >
            <Typography variant="h3">All Set?</Typography>
            {calendar?.name && (
              <Typography variant="h5" sx={{ mt: 2, mb: 4 }}>
                {isReminder ? (
                  <>
                    Do you see a reminder in your calendar for later today?
                    <br />
                    <br />
                    It should include instructions for subscribing to{" "}
                    {calendar.name} on desktop and syncing on mobile.
                  </>
                ) : (
                  <>
                    Do you see <b>{calendar.name}</b> in your list of calendars
                    {mostRecentEventDate ? (
                      <>
                        {" "}
                        and an event on <b>{mostRecentEventDate}</b>?
                      </>
                    ) : (
                      "?"
                    )}
                  </>
                )}
              </Typography>
            )}
            <Button
              variant="contained"
              size="grown"
              sx={{
                width: "100%",
                display: "flex",
                justifySelf: "center",
                mb: 2,
              }}
              onClick={handleConfirm}
              disabled={isVerifying}
            >
              YES, I SEE IT
            </Button>
            {!showTroubleshooting && (
              <Button
                variant="outlined"
                size="grown"
                sx={{ width: "100%", display: "flex", justifySelf: "center" }}
                onClick={() => {
                  customEvent({
                    name: `user_click_cal_help_${user.provider}`,
                    category: "SubscribingCalendar",
                    label: "Click Verify Calendar Help Button",
                    data: {
                      user_id: user?._id,
                      user_email: user?.email,
                    },
                  });
                  setShowTroubleshooting(true);
                }}
              >
                NO. HELP ME TROUBLESHOOT
              </Button>
            )}

            <TroubleshootingSection
              show={showTroubleshooting}
              provider={selectedProvider}
              onProviderChange={setSelectedProvider}
              calendar={calendar}
              user={user}
              onCopyLink={handleCopyToClipboard}
            />
          </Box>
        </InstructionsLayout>
      )}
      <SignInDialog
        open={openSignInDialog}
        handleClose={() => setOpenSignInDialog(false)}
        hideCloseButton={true}
        disableClickOutside={true}
        headContent={{ mainTitle: guestMainTitle, subtitle: guestSubtitle }}
      />
    </>
  );
};

export default SimpleVerifyCalendarSubscription;
